// The item sheet — one scrolling surface for all four item types (generic,
// pizza, half-and-half, bundle). Modifier semantics: min>0 = Required
// (error state until met), max=1 renders radios (tapping the selected one
// deselects), defaults come pre-picked. Pizza: sizes as cards, the size's
// bases, remove-a-default and add-a-topping priced per size. Half-and-half:
// two halves from the pizzas the server says are eligible, priced as the
// dearest half + the size's split charge. Bundle: one section per slot,
// steppers where the slot allows repeats; a pick customises in place
// (a pizza pick: remove a default, add toppings priced at the SLOT's size,
// its base and modifiers; a generic pick: its modifiers), and a pizza slot
// that allows it takes one half-and-half pick instead, its base chosen once
// for the disc. The add bar reprices
// live by the server's own rules (order_pricing.rs); the server remains
// the authority at quote and placement.

import { Fragment, useMemo, useState } from 'react';
import {
  itemsById, money, sizePriceOf,
  type BundleSlot, type Channel, type MenuDocument, type MenuItemView, type WireItem, type WireLine, type WireModifier, type WirePizza, type WireSlotPick,
} from '../lib/api';
import { addLine } from '../lib/cart';

type Group = { modifierId: string; name: string; min: number; max: number; options: { id: string; name: string; extra: number; isDefault: boolean }[] };

function groupsFor(view: MenuItemView, menu: MenuDocument): Group[] {
  const mods = new Map(menu.modifiers.map((m) => [m.modifierId, m]));
  const cap = (n: number) => (n <= 0 ? Infinity : n);
  const out: Group[] = [];
  for (const att of view.modifiers) {
    const m = mods.get(att.modifierId);
    if (!m) continue;
    out.push({
      modifierId: m.modifierId,
      name: m.name,
      min: att.minSelectOverride ?? m.minSelect,
      max: cap(att.maxSelectOverride ?? m.maxSelect),
      options: m.options.filter((o) => o.optionId).map((o) => ({ id: o.optionId!, name: o.name, extra: o.extraChargeInCents, isDefault: o.isDefault })),
    });
  }
  return out;
}

/** The defaults pre-picked, per group (capped at the group's max). */
function defaultPicked(groups: Group[]): Record<string, Record<string, true>> {
  return Object.fromEntries(groups.map((g) => [g.modifierId, Object.fromEntries(g.options.filter((o) => o.isDefault).slice(0, g.max === Infinity ? undefined : g.max).map((o) => [o.id, true]))]));
}

/** Tap an option: max=1 behaves as a radio that deselects, otherwise a capped checkbox. */
function toggleIn(sel: Record<string, Record<string, true>>, g: Group, id: string): Record<string, Record<string, true>> {
  const cur = { ...(sel[g.modifierId] ?? {}) };
  if (cur[id]) delete cur[id];
  else if (g.max === 1) return { ...sel, [g.modifierId]: { [id]: true } };
  else if (Object.keys(cur).length >= g.max) return sel;
  else cur[id] = true;
  return { ...sel, [g.modifierId]: cur };
}

const modsOf = (groups: Group[], sel: Record<string, Record<string, true>>): WireModifier[] =>
  groups.flatMap((g) => Object.keys(sel[g.modifierId] ?? {}).map((optionId) => ({ modifierId: g.modifierId, optionId })));
const modsExtra = (groups: Group[], sel: Record<string, Record<string, true>>): number =>
  groups.reduce((n, g) => n + Object.keys(sel[g.modifierId] ?? {}).reduce((m, id) => m + (g.options.find((o) => o.id === id)?.extra ?? 0), 0), 0);
const shortIn = (groups: Group[], sel: Record<string, Record<string, true>>): boolean => groups.some((g) => Object.keys(sel[g.modifierId] ?? {}).length < g.min);

/** A bundle pick's own customisation: base and toppings at the slot's size (pizza; null base = the house base) and the item's modifiers. */
type PickCfg = { base: string | null; add: Record<string, number>; remove: string[]; mods: Record<string, Record<string, true>> };
/** A pizza slot holding one half-and-half pick; the base is the disc's (null = the house base). */
type HalfPick = { first: string | null; second: string | null; base: string | null };

const EMPTY_TOPPINGS = { add: [], remove: [] };

export function ItemSheet(props: { view: MenuItemView; menu: MenuDocument; channel: Channel; storeKey: string; onClose: () => void }) {
  const { view, menu, channel } = props;
  const all = useMemo(() => itemsById(menu), [menu]);
  const groups = useMemo(() => groupsFor(view, menu), [view, menu]);
  const sizesById = useMemo(() => new Map(menu.sizes.map((s) => [s.sizeId, s])), [menu]);
  const basesById = useMemo(() => new Map(menu.bases.map((b) => [b.baseId, b])), [menu]);
  const toppingsById = useMemo(() => new Map(menu.toppings.map((t) => [t.toppingId, t])), [menu]);

  const [qty, setQty] = useState(1);
  const [tried, setTried] = useState(false);
  // modifiers: group → option → picked (defaults pre-picked)
  const [picked, setPicked] = useState<Record<string, Record<string, true>>>(() => defaultPicked(groups));
  // pizza / half-and-half
  const pinnedSize = view.itemType === 'half_and_half' ? view.sizeId : view.itemType === 'pizza' && view.sizes.length === 1 ? view.sizes[0].sizeId : null;
  const [sizeId, setSizeId] = useState<string | null>(pinnedSize);
  const sizeBases = sizeId ? (sizesById.get(sizeId)?.bases ?? []) : [];
  const [baseChoice, setBaseChoice] = useState<string | null>(null);
  const baseId = baseChoice ?? sizeBases[0]?.baseId ?? null; // the house base is first
  const [addTops, setAddTops] = useState<Record<string, number>>({});
  const [removeTops, setRemoveTops] = useState<string[]>([]);
  const [halves, setHalves] = useState<(string | null)[]>([null, null]);
  // bundle: slot → item → quantity (defaults pre-picked)
  const slots = view.itemType === 'bundle' ? view.slots : [];
  // a price row fixes the bundle's price (picks add only extras); without one the picks are priced
  const fixedBundle = view.itemType === 'bundle' && view.price !== null;
  const slotDefaults = (s: BundleSlot) => Object.fromEntries(s.options.filter((o) => o.isDefault).slice(0, s.maxSelect).map((o) => [o.itemId, 1]));
  const [picks, setPicks] = useState<Record<string, Record<string, number>>>(() => Object.fromEntries(slots.map((s) => [s.slotId!, slotDefaults(s)])));
  // a pick's customisation, keyed `slot|item` (a half: `slot|first` / `slot|second`)
  const [pickCfg, setPickCfg] = useState<Record<string, PickCfg>>({});
  const [halfPicks, setHalfPicks] = useState<Record<string, HalfPick>>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const groupsOf = useMemo(() => {
    const cache = new Map<string, Group[]>();
    return (itemId: string): Group[] => {
      let g = cache.get(itemId);
      if (!g) {
        const it = all.get(itemId);
        g = it ? groupsFor(it, menu) : [];
        cache.set(itemId, g);
      }
      return g;
    };
  }, [all, menu]);
  const freshCfg = (itemId: string): PickCfg => ({ base: null, add: {}, remove: [], mods: defaultPicked(groupsOf(itemId)) });
  const cfgOf = (key: string, itemId: string): PickCfg => pickCfg[key] ?? freshCfg(itemId);
  const editCfg = (key: string, itemId: string, f: (c: PickCfg) => PickCfg) => setPickCfg((prev) => ({ ...prev, [key]: f(prev[key] ?? freshCfg(itemId)) }));
  const dropCfg = (match: (key: string) => boolean) => setPickCfg((prev) => Object.fromEntries(Object.entries(prev).filter(([k]) => !match(k))));

  // ── selection helpers ─────────────────────────────────────────────
  const countIn = (g: Group) => Object.keys(picked[g.modifierId] ?? {}).length;
  const toggleOption = (g: Group, id: string) => setPicked((prev) => toggleIn(prev, g, id));
  const slotCount = (slotId: string) => Object.values(picks[slotId] ?? {}).reduce((n, q) => n + q, 0) + (halfPicks[slotId] ? 1 : 0);
  function bumpPick(slotId: string, itemId: string, delta: number, max: number, allowRepeat: boolean) {
    setPicks((prev) => {
      const cur = { ...(prev[slotId] ?? {}) };
      const total = Object.values(cur).reduce((n, q) => n + q, 0);
      if (delta > 0) {
        if (!allowRepeat && cur[itemId]) return prev;
        if (max === 1 && !cur[itemId]) return { ...prev, [slotId]: { [itemId]: 1 } };
        if (total >= max) return prev;
        cur[itemId] = (cur[itemId] ?? 0) + 1;
      } else {
        const next = (cur[itemId] ?? 0) - 1;
        if (next <= 0) delete cur[itemId];
        else cur[itemId] = next;
      }
      return { ...prev, [slotId]: cur };
    });
    // a pick that leaves the slot forgets its customisation
    if (delta < 0 && (picks[slotId]?.[itemId] ?? 0) <= 1) dropCfg((k) => k === `${slotId}|${itemId}`);
    // a radio slot swapping its pick: the old pick's customisation goes with it
    if (delta > 0 && max === 1) dropCfg((k) => k.startsWith(`${slotId}|`) && k !== `${slotId}|${itemId}`);
  }

  // ── half-and-half in a pizza slot ─────────────────────────────────
  const slotSize = (s: BundleSlot) => (s.sizeId ? sizesById.get(s.sizeId) : undefined);
  /** A pizza pick's base at a size: the chosen one, else the house base (the first); null when the size offers none. */
  const baseAt = (sizeId: string | null, chosen: string | null) => {
    const bases = sizeId ? (sizesById.get(sizeId)?.bases ?? []) : [];
    return bases.find((b) => b.baseId === chosen) ?? bases[0] ?? null;
  };
  function chooseDiscBase(slotId: string, baseId: string) {
    setHalfPicks((prev) => (prev[slotId] ? { ...prev, [slotId]: { ...prev[slotId], base: baseId } } : prev));
  }
  /** The slot's options that may fill a half at its size — null when the slot cannot be split. */
  function halfCandidatesOf(s: BundleSlot): MenuItemView[] | null {
    if (s.slotType !== 'pizza' || !s.allowHalfAndHalf || s.minSelect > 1 || !slotSize(s)?.halfAndHalfEnabled) return null;
    const out = s.options
      .map((o) => all.get(o.itemId))
      .filter((p): p is MenuItemView => !!p && p.itemType === 'pizza' && !p.soldOut && p.sizes.some((z) => z.sizeId === s.sizeId && z.halfAndHalfAvailable));
    return out.length >= 2 ? out : null;
  }
  function setHalfMode(s: BundleSlot, on: boolean) {
    const slotId = s.slotId!;
    dropCfg((k) => k.startsWith(`${slotId}|`));
    setHalfPicks((prev) => {
      const n = { ...prev };
      if (on) n[slotId] = { first: null, second: null, base: null };
      else delete n[slotId];
      return n;
    });
    setPicks((prev) => ({ ...prev, [slotId]: on ? {} : slotDefaults(s) }));
  }
  function chooseHalf(slotId: string, which: 'first' | 'second', itemId: string) {
    dropCfg((k) => k === `${slotId}|${which}`);
    setHalfPicks((prev) => {
      const cur = prev[slotId] ?? { first: null, second: null, base: null };
      return { ...prev, [slotId]: { ...cur, [which]: cur[which] === itemId ? null : itemId } };
    });
  }

  // ── price (display; the server prices the order) ──────────────────
  const toppingPrice = (toppingId: string, forSize: string) => toppingsById.get(toppingId)?.prices.find((p) => p.sizeId === forSize)?.priceInCents ?? 0;
  const baseExtra = sizeBases.find((b) => b.baseId === baseId)?.extraChargeInCents ?? 0;
  const unitPrice = useMemo(() => {
    let cents = 0;
    if (view.itemType === 'pizza') {
      if (sizeId) {
        cents += (sizePriceOf(view, sizeId) ?? 0) + baseExtra;
        for (const [t, q] of Object.entries(addTops)) cents += toppingPrice(t, sizeId) * q;
      }
    } else if (view.itemType === 'half_and_half') {
      const size = sizesById.get(view.sizeId);
      let dearest = 0;
      for (const h of halves) {
        const half = h ? all.get(h) : undefined;
        if (half) dearest = Math.max(dearest, sizePriceOf(half, view.sizeId) ?? 0);
      }
      if (halves.some(Boolean)) cents += dearest + (size?.halfAndHalfExtraChargeInCents ?? 0) + baseExtra;
    } else if (view.itemType === 'bundle') {
      // order_pricing.rs: (price row ? bundle price : Σ pick item prices) + Σ pick extras + Σ pick customisations
      if (fixedBundle) cents += view.channelPriceInCents ?? 0;
      const optExtra = (s: BundleSlot, itemId: string | null) => s.options.find((o) => o.itemId === itemId)?.extraChargeInCents ?? 0;
      const pizzaCustom = (key: string, itemId: string, size: string) => {
        const c = cfgOf(key, itemId);
        return Object.entries(c.add).reduce((n, [t, q]) => n + toppingPrice(t, size) * q, 0) + modsExtra(groupsOf(itemId), c.mods);
      };
      for (const s of slots) {
        const slotId = s.slotId!;
        const size = s.sizeId ?? '';
        for (const [itemId, q] of Object.entries(picks[slotId] ?? {})) {
          const it = all.get(itemId);
          let each = optExtra(s, itemId);
          if (s.slotType === 'pizza') {
            // a pizza pick carries its base (the house base unless chosen) and its own adds and modifiers
            each += (fixedBundle || !it ? 0 : (sizePriceOf(it, size) ?? 0)) + (baseAt(s.sizeId, cfgOf(`${slotId}|${itemId}`, itemId).base)?.extraChargeInCents ?? 0) + pizzaCustom(`${slotId}|${itemId}`, itemId, size);
          } else {
            each += (fixedBundle ? 0 : (it?.channelPriceInCents ?? 0)) + modsExtra(groupsOf(itemId), cfgOf(`${slotId}|${itemId}`, itemId).mods);
          }
          cents += each * q;
        }
        const hp = halfPicks[slotId];
        if (hp?.first && hp.second) {
          const [a, b] = [all.get(hp.first), all.get(hp.second)];
          // the dearer half's extra + the split charge + the disc's base; unfixed, the dearer half's size price too
          cents += Math.max(optExtra(s, hp.first), optExtra(s, hp.second)) + (slotSize(s)?.halfAndHalfExtraChargeInCents ?? 0) + (baseAt(s.sizeId, hp.base)?.extraChargeInCents ?? 0);
          if (!fixedBundle) cents += Math.max(a ? (sizePriceOf(a, size) ?? 0) : 0, b ? (sizePriceOf(b, size) ?? 0) : 0);
          cents += pizzaCustom(`${slotId}|first`, hp.first, size) + pizzaCustom(`${slotId}|second`, hp.second, size);
        }
      }
    } else {
      cents += view.channelPriceInCents ?? 0;
    }
    for (const g of groups) for (const id of Object.keys(picked[g.modifierId] ?? {})) cents += g.options.find((o) => o.id === id)?.extra ?? 0;
    return cents;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, sizeId, baseExtra, addTops, halves, picks, picked, groups, slots, all, sizesById, pickCfg, halfPicks, fixedBundle, groupsOf]);

  // ── validity ──────────────────────────────────────────────────────
  const shortGroups = groups.filter((g) => countIn(g) < g.min);
  const needsSize = view.itemType === 'pizza' && !sizeId;
  const needsHalves = view.itemType === 'half_and_half' && halves.some((h) => !h);
  // a slot is short when underfilled, when its half-and-half lacks a half, or when a pick's required modifier is unmet
  const shortSlots = slots.filter((s) => {
    const slotId = s.slotId!;
    if (slotCount(slotId) < s.minSelect) return true;
    const hp = halfPicks[slotId];
    if (hp && (!hp.first || !hp.second)) return true;
    const keyed: [string, string][] = Object.keys(picks[slotId] ?? {}).map((id) => [`${slotId}|${id}`, id]);
    if (hp?.first) keyed.push([`${slotId}|first`, hp.first]);
    if (hp?.second) keyed.push([`${slotId}|second`, hp.second]);
    return keyed.some(([k, id]) => shortIn(groupsOf(id), cfgOf(k, id).mods));
  });
  const valid = shortGroups.length === 0 && !needsSize && !needsHalves && shortSlots.length === 0;

  // ── add to cart ───────────────────────────────────────────────────
  function add() {
    setTried(true);
    if (!valid) return;
    const modifiers: WireModifier[] = [];
    for (const g of groups) for (const optionId of Object.keys(picked[g.modifierId] ?? {})) modifiers.push({ modifierId: g.modifierId, optionId });
    const bits: string[] = [];
    let item: WireItem;
    if (view.itemType === 'pizza') {
      const toppings = { add: Object.entries(addTops).map(([toppingId, quantity]) => ({ toppingId, quantity })), remove: removeTops };
      item = { itemType: 'pizza', sizeId: sizeId!, baseId, itemId: view.itemId, toppings, modifiers };
      bits.push(sizesById.get(sizeId!)?.name ?? '');
      if (sizeBases.length > 1 && baseId) bits.push(basesById.get(baseId)?.name ?? '');
      bits.push(...Object.entries(addTops).map(([t, q]) => `+ ${q > 1 ? `${q}× ` : ''}${toppingsById.get(t)?.name ?? ''}`));
      bits.push(...removeTops.map((t) => `no ${toppingsById.get(t)?.name ?? ''}`));
    } else if (view.itemType === 'half_and_half') {
      const half = (id: string) => ({ itemId: id, toppings: EMPTY_TOPPINGS, modifiers: [] });
      item = { itemType: 'half_and_half', itemId: view.itemId, baseId, first: half(halves[0]!), second: half(halves[1]!), modifiers };
      bits.push(...halves.map((h) => all.get(h!)?.name ?? ''));
      if (sizeBases.length > 1 && baseId) bits.push(basesById.get(baseId)?.name ?? '');
    } else if (view.itemType === 'bundle') {
      const wirePicks: WireSlotPick[] = [];
      const wirePizza = (key: string, itemId: string): WirePizza => {
        const c = cfgOf(key, itemId);
        return { itemId, toppings: { add: Object.entries(c.add).map(([toppingId, quantity]) => ({ toppingId, quantity })), remove: c.remove }, modifiers: modsOf(groupsOf(itemId), c.mods) };
      };
      // a base other than the house one is named: "Gluten Free"
      const baseName = (sizeId: string | null, chosen: string | null) => {
        const b = baseAt(sizeId, chosen);
        return b && b.baseId !== baseAt(sizeId, null)?.baseId ? (basesById.get(b.baseId)?.name ?? '') : '';
      };
      // "Pepperoni Pizza (Gluten Free, + Olives, no Cheese, Well done)"
      const named = (key: string, itemId: string, pizza: boolean, sizeId: string | null = null) => {
        const c = cfgOf(key, itemId);
        const own = pizza
          ? [baseName(sizeId, c.base), ...Object.entries(c.add).map(([t, q]) => `+ ${q > 1 ? `${q}× ` : ''}${toppingsById.get(t)?.name ?? ''}`), ...c.remove.map((t) => `no ${toppingsById.get(t)?.name ?? ''}`)]
          : [];
        for (const g of groupsOf(itemId)) for (const id of Object.keys(c.mods[g.modifierId] ?? {})) own.push(g.options.find((o) => o.id === id)?.name ?? '');
        const name = all.get(itemId)?.name ?? '';
        return own.filter(Boolean).length ? `${name} (${own.filter(Boolean).join(', ')})` : name;
      };
      for (const s of slots) {
        const slotId = s.slotId!;
        for (const [itemId, quantity] of Object.entries(picks[slotId] ?? {})) {
          const key = `${slotId}|${itemId}`;
          wirePicks.push(
            s.slotType === 'pizza'
              ? { slotId, quantity, pickType: 'pizza', baseId: baseAt(s.sizeId, cfgOf(key, itemId).base)?.baseId ?? null, ...wirePizza(key, itemId) }
              : { slotId, quantity, pickType: 'generic', itemId, modifiers: modsOf(groupsOf(itemId), cfgOf(key, itemId).mods) },
          );
          bits.push(`${quantity > 1 ? `${quantity}× ` : ''}${named(key, itemId, s.slotType === 'pizza', s.sizeId)}`);
        }
        const hp = halfPicks[slotId];
        if (hp?.first && hp.second) {
          wirePicks.push({ slotId, quantity: 1, pickType: 'half_and_half', baseId: baseAt(s.sizeId, hp.base)?.baseId ?? null, first: wirePizza(`${slotId}|first`, hp.first), second: wirePizza(`${slotId}|second`, hp.second) });
          const disc = baseName(s.sizeId, hp.base);
          bits.push(`Half ${named(`${slotId}|first`, hp.first, true)} / ${named(`${slotId}|second`, hp.second, true)}${disc ? `, ${disc} base` : ''}`);
        }
      }
      item = { itemType: 'bundle', itemId: view.itemId, picks: wirePicks, modifiers };
    } else {
      item = { itemType: 'generic', itemId: view.itemId, modifiers };
    }
    for (const g of groups) for (const id of Object.keys(picked[g.modifierId] ?? {})) bits.push(g.options.find((o) => o.id === id)?.name ?? '');
    const wire: WireLine = { quantity: qty, ...item };
    addLine(props.storeKey, channel, { name: view.name, itemType: view.itemType, unitPriceInCents: unitPrice, configSummary: bits.filter(Boolean).join(' · '), wire });
    props.onClose();
  }

  // ── a bundle pick, customised in place ──────────────────────────
  /** The base at the slot's size (a whole pizza pick; a half's base is the disc's), remove-a-default
   * and add-a-topping at that size (pizza picks), then the item's modifiers.
   * Folded behind "Customise" unless a required modifier still needs an answer. */
  function pickCustomiser(key: string, itemId: string, size: string | null, withBase = false) {
    const it = all.get(itemId);
    if (!it) return null;
    const pizza = it.itemType === 'pizza' && size ? it : null;
    const gs = groupsOf(itemId);
    const defaults = pizza && pizza.itemType === 'pizza' ? pizza.defaultToppings : [];
    const addable = pizza ? menu.toppings.filter((t) => !t.soldOut && !defaults.includes(t.toppingId) && t.prices.some((p) => p.sizeId === size)) : [];
    const bases = pizza && withBase ? (sizesById.get(size!)?.bases ?? []) : [];
    if (bases.length < 2 && defaults.length === 0 && addable.length === 0 && gs.length === 0) return null;
    const c = cfgOf(key, itemId);
    const short = shortIn(gs, c.mods);
    const open = expanded[key] || short;
    const base = baseAt(size, c.base);
    const edits = [...(bases.length > 1 && base && base.baseId !== bases[0].baseId ? [basesById.get(base.baseId)?.name ?? ''] : []), ...Object.keys(c.add).map((t) => `+ ${toppingsById.get(t)?.name ?? ''}`), ...c.remove.map((t) => `no ${toppingsById.get(t)?.name ?? ''}`)];
    return (
      <div className="pickcfg" data-pick={key}>
        <button className="linky pickcfg-toggle" aria-expanded={open} aria-label={`Customise ${it.name}`} onClick={() => setExpanded((p) => ({ ...p, [key]: !open }))}>
          {open ? 'Done' : 'Customise'}
          {!open && edits.length > 0 && <span className="pickcfg-sum"> · {edits.join(', ')}</span>}
        </button>
        {open && (
          <>
            {bases.length > 1 && <div className="st">Base</div>}
            {bases.length > 1 && bases.map((b) => {
              const name = basesById.get(b.baseId)?.name ?? b.baseId;
              const on = base?.baseId === b.baseId;
              return (
                <button key={b.baseId} className="optrow" aria-label={`${it.name}: Base: ${name}`} aria-pressed={on} onClick={() => editCfg(key, itemId, (x) => ({ ...x, base: b.baseId }))}>
                  <span className={'rad' + (on ? ' on' : '')} />
                  {name}
                  {b.extraChargeInCents > 0 && <span className="price">+ {money(b.extraChargeInCents)}</span>}
                </button>
              );
            })}
            {defaults.length > 0 && <div className="st">Remove</div>}
            {defaults.map((tid) => {
              const on = c.remove.includes(tid);
              const name = toppingsById.get(tid)?.name ?? tid;
              return (
                <button key={tid} className="optrow" aria-label={`${it.name}: Remove ${name}`} aria-pressed={on}
                  onClick={() => editCfg(key, itemId, (x) => ({ ...x, remove: on ? x.remove.filter((r) => r !== tid) : [...x.remove, tid] }))}>
                  <span className={'chk' + (on ? ' on' : '')} />
                  {name}
                </button>
              );
            })}
            {addable.length > 0 && <div className="st">Add toppings</div>}
            {addable.map((t) => {
              const on = !!c.add[t.toppingId];
              const p = toppingPrice(t.toppingId, size!);
              return (
                <button key={t.toppingId} className="optrow" aria-label={`${it.name}: Add ${t.name}`} aria-pressed={on}
                  onClick={() => editCfg(key, itemId, (x) => { const add = { ...x.add }; if (add[t.toppingId]) delete add[t.toppingId]; else add[t.toppingId] = 1; return { ...x, add }; })}>
                  <span className={'chk' + (on ? ' on' : '')} />
                  {t.name}
                  {p > 0 && <span className="price">+ {money(p)}</span>}
                </button>
              );
            })}
            {gs.map((g) => {
              const n = Object.keys(c.mods[g.modifierId] ?? {}).length;
              return (
                <Fragment key={g.modifierId}>
                  <div className="st">{g.name}{g.min > 0 ? <span className={'badge-req' + (tried && n < g.min ? ' err' : '')}>Required</span> : <span className="badge-opt">{g.max === 1 ? 'Choose 1' : 'Optional'}</span>}</div>
                  {g.options.map((o) => {
                    const on = !!c.mods[g.modifierId]?.[o.id];
                    return (
                      <button key={o.id} className="optrow" aria-label={`${it.name}: ${g.name}: ${o.name}`} aria-pressed={on} onClick={() => editCfg(key, itemId, (x) => ({ ...x, mods: toggleIn(x.mods, g, o.id) }))}>
                        <span className={(g.max === 1 ? 'rad' : 'chk') + (on ? ' on' : '')} />
                        {o.name}
                        {o.extra > 0 && <span className="price">+ {money(o.extra)}</span>}
                      </button>
                    );
                  })}
                </Fragment>
              );
            })}
          </>
        )}
      </div>
    );
  }

  // ── render ────────────────────────────────────────────────────────
  const defaultTops = view.itemType === 'pizza' ? view.defaultToppings : [];
  const addableTops = view.itemType === 'pizza' && sizeId ? menu.toppings.filter((t) => !t.soldOut && !defaultTops.includes(t.toppingId) && t.prices.some((p) => p.sizeId === sizeId)) : [];
  const hhSize = view.itemType === 'half_and_half' ? sizesById.get(view.sizeId) : undefined;
  const halfCandidates = view.itemType === 'half_and_half' ? view.eligiblePizzaIds.map((id) => all.get(id)).filter((p): p is MenuItemView => !!p) : [];

  return (
    <div className="scrim" onClick={props.onClose}>
      <div className="sheet" role="dialog" aria-label={view.name} onClick={(e) => e.stopPropagation()} style={{ position: 'relative' }}>
        <button className="sclose" onClick={props.onClose} aria-label="Close">✕</button>
        {view.imageUrl && <div className="simg"><img src={view.imageUrl} alt="" /></div>}
        <div className="sbody">
          <div className="shead">
            <div className="nm">{view.name}</div>
            {view.description && <div className="ds">{view.description}</div>}
          </div>

          {view.itemType === 'pizza' && (
            <div className="sec" data-section="size">
              <div className="t">Size<span className={'badge-req' + (tried && needsSize ? ' err' : '')}>Required</span></div>
              <div className="sizes">
                {view.sizes.map((e) => {
                  const name = sizesById.get(e.sizeId)?.name ?? e.sizeId;
                  return (
                    <button key={e.sizeId} className={'size' + (sizeId === e.sizeId ? ' on' : '')} aria-label={`Size: ${name}`} aria-pressed={sizeId === e.sizeId}
                      onClick={() => { setSizeId(e.sizeId); setBaseChoice(null); setAddTops({}); }}>
                      <div className="s1">{name}</div>
                      <div className="s2">{money(sizePriceOf(view, e.sizeId) ?? 0)}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {sizeBases.length > 1 && (
            <div className="sec" data-section="base">
              <div className="t">Base<span className="badge-opt">Choose 1</span></div>
              <div>
                {sizeBases.map((b) => {
                  const name = basesById.get(b.baseId)?.name ?? b.baseId;
                  const on = baseId === b.baseId;
                  return (
                    <button key={b.baseId} className="optrow" aria-label={`Base: ${name}`} aria-pressed={on} onClick={() => setBaseChoice(b.baseId)}>
                      <span className={'rad' + (on ? ' on' : '')} />
                      {name}
                      {b.extraChargeInCents > 0 && <span className="price">+ {money(b.extraChargeInCents)}</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {view.itemType === 'pizza' && defaultTops.length > 0 && (
            <div className="sec" data-section="remove">
              <div className="t">Remove<span className="badge-opt">Optional</span></div>
              <div>
                {defaultTops.map((tid) => {
                  const on = removeTops.includes(tid);
                  const name = toppingsById.get(tid)?.name ?? tid;
                  return (
                    <button key={tid} className="optrow" aria-label={`Remove ${name}`} aria-pressed={on} onClick={() => setRemoveTops((p) => (on ? p.filter((x) => x !== tid) : [...p, tid]))}>
                      <span className={'chk' + (on ? ' on' : '')} />
                      {name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {addableTops.length > 0 && sizeId && (
            <div className="sec" data-section="toppings">
              <div className="t">Add toppings<span className="badge-opt">Optional</span></div>
              <div>
                {addableTops.map((t) => {
                  const q = addTops[t.toppingId] ?? 0;
                  const p = toppingPrice(t.toppingId, sizeId);
                  return (
                    <button key={t.toppingId} className="optrow" aria-label={`Add ${t.name}`} aria-pressed={q > 0}
                      onClick={() => setAddTops((prev) => { const n = { ...prev }; if (n[t.toppingId]) delete n[t.toppingId]; else n[t.toppingId] = 1; return n; })}>
                      <span className={'chk' + (q > 0 ? ' on' : '')} />
                      {t.name}
                      {p > 0 && <span className="price">+ {money(p)}</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {view.itemType === 'half_and_half' && hhSize && (
            <>
              {halves.map((half, idx) => (
                <div className="sec" data-section={`half-${idx + 1}`} key={idx}>
                  <div className="t">{idx === 0 ? '1st half' : '2nd half'}<span className={'badge-req' + (tried && !half ? ' err' : '')}>Required</span></div>
                  <div>
                    {halfCandidates.map((pz) => {
                      const on = half === pz.itemId;
                      return (
                        <button key={pz.itemId} className="optrow" aria-label={`${idx === 0 ? '1st' : '2nd'} half: ${pz.name}`} aria-pressed={on}
                          onClick={() => setHalves((p) => p.map((h, i) => (i === idx ? (on ? null : pz.itemId) : h)))}>
                          <span className={'rad' + (on ? ' on' : '')} />
                          {pz.name}
                          <span className="price">{money(sizePriceOf(pz, view.sizeId) ?? 0)}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
              <div className="sec" style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                {hhSize.name} · priced as the dearest half{hhSize.halfAndHalfExtraChargeInCents > 0 && <> + {money(hhSize.halfAndHalfExtraChargeInCents)} half-and-half charge</>}.
              </div>
            </>
          )}

          {slots.map((s) => {
            const slotId = s.slotId!;
            const short = shortSlots.includes(s);
            const halfCands = halfCandidatesOf(s);
            const hp = halfPicks[slotId];
            const split = slotSize(s)?.halfAndHalfExtraChargeInCents ?? 0;
            return (
              <div className="sec" data-section={`slot-${slotId}`} key={slotId}>
                <div className="t">{s.name}
                  {s.minSelect > 0 ? <span className={'badge-req' + (tried && short ? ' err' : '')}>{s.maxSelect > 1 ? `Choose ${s.minSelect}–${s.maxSelect}` : 'Required'}</span> : <span className="badge-opt">{s.maxSelect > 1 ? `Up to ${s.maxSelect}` : 'Optional'}</span>}
                </div>
                <div>
                  {halfCands && (
                    <button className="optrow" aria-label={`${s.name}: Half & half`} aria-pressed={!!hp} onClick={() => setHalfMode(s, !hp)}>
                      <span className={'chk' + (hp ? ' on' : '')} />
                      Half &amp; half
                      {split > 0 && <span className="price">+ {money(split)}</span>}
                    </button>
                  )}
                  {hp && halfCands
                    ? (['first', 'second'] as const).map((which) => {
                        const label = which === 'first' ? '1st half' : '2nd half';
                        const other = hp[which === 'first' ? 'second' : 'first'];
                        return (
                          <div className="pickhalf" data-section={`slot-${slotId}-${which}`} key={which}>
                            <div className="st">{label}{tried && !hp[which] && <span className="badge-req err">Required</span>}</div>
                            {halfCands.map((pz) => {
                              const on = hp[which] === pz.itemId;
                              const extra = s.options.find((o) => o.itemId === pz.itemId)?.extraChargeInCents ?? 0;
                              return (
                                <Fragment key={pz.itemId}>
                                  <button className="optrow" aria-label={`${s.name} ${label}: ${pz.name}`} aria-pressed={on} disabled={other === pz.itemId} onClick={() => chooseHalf(slotId, which, pz.itemId)}>
                                    <span className={'rad' + (on ? ' on' : '')} />
                                    {pz.name}
                                    {extra > 0 && <span className="price">+ {money(extra)}</span>}
                                  </button>
                                  {on && pickCustomiser(`${slotId}|${which}`, pz.itemId, s.sizeId)}
                                </Fragment>
                              );
                            })}
                          </div>
                        );
                      }).concat(
                        (slotSize(s)?.bases.length ?? 0) > 1 ? [
                          <div className="pickhalf" data-section={`slot-${slotId}-base`} key="base">
                            <div className="st">Base</div>
                            {slotSize(s)!.bases.map((b) => {
                              const name = basesById.get(b.baseId)?.name ?? b.baseId;
                              const on = baseAt(s.sizeId, hp.base)?.baseId === b.baseId;
                              return (
                                <button key={b.baseId} className="optrow" aria-label={`${s.name} base: ${name}`} aria-pressed={on} onClick={() => chooseDiscBase(slotId, b.baseId)}>
                                  <span className={'rad' + (on ? ' on' : '')} />
                                  {name}
                                  {b.extraChargeInCents > 0 && <span className="price">+ {money(b.extraChargeInCents)}</span>}
                                </button>
                              );
                            })}
                          </div>,
                        ] : [],
                      )
                    : s.options.map((o) => {
                        const name = all.get(o.itemId)?.name ?? o.itemId;
                        const q = picks[slotId]?.[o.itemId] ?? 0;
                        return (
                          <Fragment key={o.itemId}>
                            <button className="optrow" aria-label={`${s.name}: ${name}`} aria-pressed={q > 0}
                              onClick={() => (q > 0 && !s.allowRepeat ? bumpPick(slotId, o.itemId, -1, s.maxSelect, s.allowRepeat) : bumpPick(slotId, o.itemId, +1, s.maxSelect, s.allowRepeat))}>
                              <span className={(s.maxSelect === 1 ? 'rad' : 'chk') + (q > 0 ? ' on' : '')} />
                              {name}
                              {s.allowRepeat && q > 0 ? (
                                <span className="stepmini">
                                  <button aria-label={`Fewer ${name}`} onClick={(e) => { e.stopPropagation(); bumpPick(slotId, o.itemId, -1, s.maxSelect, true); }}>−</button>
                                  {q}
                                  <button aria-label={`More ${name}`} onClick={(e) => { e.stopPropagation(); bumpPick(slotId, o.itemId, +1, s.maxSelect, true); }}>+</button>
                                </span>
                              ) : (
                                o.extraChargeInCents > 0 && <span className="price">+ {money(o.extraChargeInCents)}</span>
                              )}
                            </button>
                            {q > 0 && pickCustomiser(`${slotId}|${o.itemId}`, o.itemId, s.slotType === 'pizza' ? s.sizeId : null, true)}
                          </Fragment>
                        );
                      })}
                </div>
              </div>
            );
          })}

          {groups.map((g) => {
            const short = countIn(g) < g.min;
            return (
              <div className="sec" data-section={`modifier-${g.modifierId}`} key={g.modifierId}>
                <div className="t">{g.name}
                  {g.min > 0 ? <span className={'badge-req' + (tried && short ? ' err' : '')}>Required</span> : <span className="badge-opt">{g.max === 1 ? 'Choose 1' : g.max !== Infinity ? `Up to ${g.max}` : 'Optional'}</span>}
                </div>
                <div>
                  {g.options.map((o) => {
                    const on = !!picked[g.modifierId]?.[o.id];
                    return (
                      <button key={o.id} className="optrow" aria-label={`${g.name}: ${o.name}`} aria-pressed={on} onClick={() => toggleOption(g, o.id)}>
                        <span className={(g.max === 1 ? 'rad' : 'chk') + (on ? ' on' : '')} />
                        {o.name}
                        {o.extra > 0 && <span className="price">+ {money(o.extra)}</span>}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}

          <div style={{ height: 14 }} />
        </div>
        <div className="sfoot">
          <div className="qty">
            <button aria-label="Fewer" onClick={() => setQty((q) => Math.max(1, q - 1))}>−</button>
            <span data-qty={qty}>{qty}</span>
            <button aria-label="More" onClick={() => setQty((q) => q + 1)}>+</button>
          </div>
          <button className="btn-add" onClick={add} disabled={tried && !valid} data-unit-price={unitPrice}>
            <span>Add to order</span>
            {unitPrice > 0 && <span className="p">{money(unitPrice * qty)}</span>}
          </button>
        </div>
      </div>
    </div>
  );
}
